import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { X, Loader, Plus, Trash2 } from 'lucide-react';

const OWNERSHIP_OPTIONS = [
  { value: 'user', label: 'User' },
  { value: 'company', label: 'Company' },
];

const NewInstallationModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    username: '', name: '', phone: '', cnic: '', address: '',
    package: null, area: null,
    expiry_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    discount_type: 'none',
    custom_price: '',
  });

  const [items, setItems] = useState([
    { description: 'Router', actual_amount: '', discount_amount: '0', ownership: 'user' },
    { description: 'Wire', actual_amount: '', discount_amount: '0', ownership: 'company' },
  ]);

  const [packages, setPackages] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pkgRes, areaRes] = await Promise.all([
          api.get('/packages/'),
          api.get('/areas/'),
        ]);
        setPackages(pkgRes.data.results || []);
        setAreas(areaRes.data.results || []);
      } catch (err) {
        setError('Failed to load packages and areas');
      } finally {
        setFetchingData(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setItems(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const addItem = () => {
    setItems(prev => [...prev, { description: '', actual_amount: '', discount_amount: '0', ownership: 'user' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(prev => prev.filter((_, i) => i !== index));
    }
  };

  const calculatePayable = (item) => {
    const actual = parseFloat(item.actual_amount) || 0;
    const discount = parseFloat(item.discount_amount) || 0;
    return Math.max(0, actual - discount);
  };

  const totalPayable = items.reduce((sum, item) => sum + calculatePayable(item), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // 1. Create customer with status 'pending'
      const customerRes = await api.post('/customers/', {
        ...formData,
        status: 'pending',
        package: formData.package || null,
        area: formData.area || null,
      });

      const customerId = customerRes.data.id;

      // 2. Create installation invoice with items
      const invoiceItems = items
        .filter(item => item.description && item.actual_amount)
        .map(item => ({
          description: item.description,
          actual_amount: parseFloat(item.actual_amount),
          discount_amount: parseFloat(item.discount_amount) || 0,
          payable_amount: calculatePayable(item),
          ownership: item.ownership,
        }));

      if (invoiceItems.length > 0) {
        await api.post('/installation-invoices/', {
          customer: customerId,
          items: invoiceItems,
          notes: '',
        });
      }

      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.username?.[0] || 'Failed to create installation');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  if (fetchingData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-8">
          <Loader className="animate-spin text-purple-600 mx-auto" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-xl font-bold text-gray-900">New Installation</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{error}</div>
          )}

          {/* Customer Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Customer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Username *</label>
                <input name="username" value={formData.username} onChange={handleChange}
                  className={inputClass} required placeholder="e.g., john123" />
              </div>
              <div>
                <label className={labelClass}>Full Name *</label>
                <input name="name" value={formData.name} onChange={handleChange}
                  className={inputClass} required placeholder="e.g., John Doe" />
              </div>
              <div>
                <label className={labelClass}>Phone *</label>
                <input name="phone" value={formData.phone} onChange={handleChange}
                  className={inputClass} required placeholder="e.g., 03001234567" />
              </div>
              <div>
                <label className={labelClass}>CNIC</label>
                <input name="cnic" value={formData.cnic} onChange={handleChange}
                  className={inputClass} placeholder="e.g., 35201-1234567-1" />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Address</label>
                <input name="address" value={formData.address} onChange={handleChange}
                  className={inputClass} placeholder="e.g., House 123, Street 5, Area" />
              </div>
              <div>
                <label className={labelClass}>Area</label>
                <select name="area" value={formData.area || ''} onChange={handleChange} className={inputClass}>
                  <option value="">Select Area</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Package *</label>
                <select name="package" value={formData.package || ''} onChange={handleChange} className={inputClass} required>
                  <option value="">Select Package</option>
                  {packages.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.bandwidth}) - Rs. {p.monthly_price}/mo
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Installation Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-800">Installation Items</h3>
              <button type="button" onClick={addItem}
                className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800">
                <Plus size={16} /> Add Item
              </button>
            </div>

            <div className="space-y-3">
              {/* Header row */}
              <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 uppercase">
                <div className="col-span-4">Description</div>
                <div className="col-span-2">Actual (Rs.)</div>
                <div className="col-span-2">Discount (Rs.)</div>
                <div className="col-span-2">Payable (Rs.)</div>
                <div className="col-span-1">Owner</div>
                <div className="col-span-1"></div>
              </div>

              {items.map((item, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start bg-gray-50 p-3 rounded-lg">
                  <div className="md:col-span-4">
                    <input
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      className={inputClass}
                      placeholder="e.g., Router, Wire, Labor"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <input
                      type="number"
                      value={item.actual_amount}
                      onChange={(e) => handleItemChange(index, 'actual_amount', e.target.value)}
                      className={inputClass}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <input
                      type="number"
                      value={item.discount_amount}
                      onChange={(e) => handleItemChange(index, 'discount_amount', e.target.value)}
                      className={inputClass}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700">
                      Rs. {calculatePayable(item).toLocaleString()}
                    </div>
                  </div>
                  <div className="md:col-span-1">
                    <select
                      value={item.ownership}
                      onChange={(e) => handleItemChange(index, 'ownership', e.target.value)}
                      className={inputClass}
                    >
                      {OWNERSHIP_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-1 flex items-center justify-center">
                    {items.length > 1 && (
                      <button type="button" onClick={() => removeItem(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="mt-4 flex justify-end">
              <div className="bg-purple-50 border border-purple-200 rounded-lg px-6 py-3">
                <span className="text-sm text-purple-700">Total Installation: </span>
                <span className="text-xl font-bold text-purple-900">Rs. {totalPayable.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
              {loading && <Loader size={16} className="animate-spin" />}
              Create Installation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewInstallationModal;
